import { createFileRoute, Navigate } from '@tanstack/react-router';
import { z } from 'zod';

import { searchReturnTo } from '../../components/searchReturnTo';
import { useAuth } from '../../context/AuthContext';
import { useEventQuery } from '../../hooks/react-query/useEventsQuery';
import {
  useProjectEventsBatchGetQuery,
  type UseProjectEventsBatchGetQueryArgs,
} from '../../hooks/react-query/useProjectEventsQuery';
import { ServiceCall } from '../../modules/ComponentsLibrary/ServiceCall';
import { legacyJobTabIndexToJobDetailsTab } from '../../modules/ComponentsLibrary/ServiceCall/tabUtils';

export const Route = createFileRoute('/jobs/$jobId')({
  component: RouteComponent,
  params: z.object({
    jobId: z.coerce
      .number()
      .min(0)
      .refine((val) => !isNaN(val), {
        message: 'Job ID must be a valid number',
      }),
  }),
  validateSearch: z.object({
    defaultTab: z.coerce.number().min(0).optional().default(0),
    /** `legacy` pins ServiceCall; `new` pins Job Details (overrides profile preference). */
    flow: z.enum(['legacy', 'new']).optional(),
    /** Passed on to the job record, whose phone header's Back returns to this search. */
    returnTo: searchReturnTo,
  }),
  head(ctx) {
    return {
      meta: [{ title: `Job View - ${ctx.params.jobId}` }],
    };
  },
});

function RouteComponent() {
  const { user } = useAuth();
  const { jobId } = Route.useParams();
  const { defaultTab, flow, returnTo } = Route.useSearch();

  /** Event is only needed for the project redirect; legacy flow skips it. */
  const shouldResolveRoute = jobId > 0 && flow !== 'legacy';

  const { data: event, isLoading: isEventLoading } = useEventQuery({
    filter: { id: jobId, onlyEvents: true },
    enabled: shouldResolveRoute,
  });

  /** Event.parentId is the ProjectEvent row id (see useEnhancedProjectData). */
  const projectEventParentId = event?.parentId ?? 0;
  const shouldLoadProjectEvent = shouldResolveRoute && projectEventParentId > 0;

  const projectEventsBatchArgs: UseProjectEventsBatchGetQueryArgs = {
    filter: { id: projectEventParentId },
    enabled: shouldLoadProjectEvent,
  };
  const { data: projectData, isLoading: isProjectEventLoading } =
    useProjectEventsBatchGetQuery(projectEventsBatchArgs);

  const projectEventRow = projectData?.results?.[0];

  if (jobId <= 0) {
    return <ServiceCall serviceCallId={jobId} defaultTab={defaultTab} />;
  }

  if (flow === 'legacy') {
    return <ServiceCall serviceCallId={jobId} defaultTab={defaultTab} />;
  }

  if (shouldResolveRoute && isEventLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (shouldLoadProjectEvent && isProjectEventLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (shouldResolveRoute && event && projectEventParentId > 0 && projectEventRow) {
    return <Navigate to="/project/$projectId" params={{ projectId: jobId }} replace />;
  }

  const shouldOpenJobDetails = flow === 'new' || !user.preferAdminView;

  if (shouldOpenJobDetails) {
    const tab = legacyJobTabIndexToJobDetailsTab(defaultTab);
    return (
      <Navigate
        to="/job-details/$eventId"
        params={{ eventId: jobId }}
        search={{
          ...(defaultTab === 0 ? {} : { tab }),
          ...(flow === 'new' ? { flow } : {}),
          ...(returnTo ? { returnTo } : {}),
        }}
        replace
      />
    );
  }

  return <ServiceCall serviceCallId={jobId} defaultTab={defaultTab} />;
}
